import React, { useEffect } from "react";
import { useAppSelector } from "../../../hooks/hooks";
import {
  selectVehicleReport,
  selectVehicleReportStatus,
} from "../../../features/reports/reportsSlice";
import {
  CollapsableItemList,
  Container,
  Icon,
  IconList,
  Layout,
  Padding,
  Text,
} from "@kidslivesafe/sc-react";

import { VehicleReportType } from "../../../features/reports/reportTypes";
import LoadingContainerLineItem from "../../../components/UIComponents/LoadingContainerLineItem/LoadingContainerLineItem";
import { camelCaseToTitle } from "../../../utils";

const SafetyRatings: React.FC<{
  containerId: string;
  printReady?: boolean;
}> = ({ containerId, printReady }) => {
  const [safetyRatings, setSafetyRatings] = React.useState<{
    [key: string]: string;
  }>({});

  let pulledReport = useAppSelector(selectVehicleReport) as VehicleReportType;
  const vehicleReportStatus = useAppSelector(selectVehicleReportStatus);

  const loading = vehicleReportStatus === "loading";

  useEffect(() => {
    if (pulledReport && pulledReport.ratings) {
      setSafetyRatings(pulledReport.ratings || {});
    }
  }, [pulledReport]);

  let orderedSafetyRatings: { [key: string]: string | undefined } = {};
  orderedSafetyRatings["overall"] = safetyRatings["overall"] || undefined;
  orderedSafetyRatings["front"] = safetyRatings["front"] || undefined;
  orderedSafetyRatings["side"] = safetyRatings["side"] || undefined;
  orderedSafetyRatings["rollover"] = safetyRatings["rollover"] || undefined;

  Object.entries(safetyRatings).forEach(([key, value], _index) => {
    if (
      key === "overall" ||
      key === "front" ||
      key === "side" ||
      key === "rollover"
    ) {
      // Skip these keys as they are handled separately
    } else {
      orderedSafetyRatings[key] = value;
    }
  });

  return (
    <Padding
      space="none"
      breakpointStyles={{
        mobile: {
          left: "md",
          right: "md",
        },
      }}
    >
      <Container
        containerId={containerId}
        containerTitle={`Safety Ratings ${
          loading
            ? ""
            : `(${
                orderedSafetyRatings && Object.keys(orderedSafetyRatings)
                  ? Object.keys(orderedSafetyRatings).length
                  : 0
              })`
        }`}
      >
        <Container>
          <Padding space="sm">
            <Layout type="flex" flexProps={{ gap: "xs", direction: "column" }}>
              {loading || (pulledReport && pulledReport.ratings) ? (
                <>
                  <Layout
                    type="flex"
                    flexProps={{ gap: "xxs", alignItems: "center" }}
                  >
                    <Text tag="div" size="xs">
                      Source: NHTSA & IIHS
                    </Text>
                  </Layout>
                </>
              ) : null}
            </Layout>
          </Padding>
        </Container>
        {loading ? (
          <LoadingContainerLineItem
            lineItemHeader
            lineItemSubHeader
            numberToLoad={3}
          />
        ) : pulledReport && pulledReport.ratings ? (
          <>
            <Padding space="md" top="none">
              <Container border={true}>
                <CollapsableItemList
                  numberToShow={5}
                  isOpen={printReady ? true : false}
                >
                  {Object.entries(orderedSafetyRatings).map(
                    ([key, value], _index) => (
                      <Container key={key}>
                        <Padding space="sm">
                          <Layout
                            type="grid"
                            gridProps={{ gap: "xxs", alignItems: "center" }}
                          >
                            <Text size="sm">{camelCaseToTitle(key)}</Text>
                            <Text
                              size="sm"
                              textWrap="nowrap"
                              wordBreak="keep-all"
                              fontWeight="bold"
                            >
                              {value && /^\d$/.test(value)
                                ? Array.from({ length: 5 }).map((_, index) => (
                                    <Icon
                                      key={`star-${index}`}
                                      name={
                                        index + 1 <= parseInt(value)
                                          ? IconList.IconStar
                                          : IconList.IconStarHollow
                                      }
                                      size={16}
                                      iconColor="gold"
                                      block="inline"
                                    />
                                  ))
                                : value
                                  ? value
                                  : "N/A"}
                            </Text>
                          </Layout>
                        </Padding>
                      </Container>
                    ),
                  )}
                </CollapsableItemList>
              </Container>
            </Padding>
          </>
        ) : (
          <Padding space="md">
            <Text size="sm" fontWeight="bold">
              There are no safety ratings available for this vehicle.
            </Text>
          </Padding>
        )}
      </Container>
    </Padding>
  );
};

export default SafetyRatings;
